"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.registerCrudException = exports.ForbiddenError = exports.ValidationError = exports.ConflictError = exports.NotFoundError = exports.CrudError = void 0;
exports.throwOnCrudError = throwOnCrudError;
const contracts_core_1 = require("@omniflow/contracts-core");
/**
 * Базовый класс типизированных CRUD-исключений. Каждый подкласс соответствует
 * одному `errorCode` из reply'я `{isOk:false, errorCode, error?, details?}`.
 *
 * Зачем: client-фасады (`@omniflow/client-<domain>`) после `bus.request(...)`
 * вызывают `throwOnCrudError(reply)` — тот видит `errorCode` и кидает
 * соответствующий typed exception. На стороне caller'а ловится через `instanceof
 * NotFoundError` без парсинга строк.
 */
class CrudError extends contracts_core_1.OmniError {
    code;
    details;
    constructor(code, message, details) {
        super(message ?? code);
        this.code = code;
        this.details = details;
        this.name = new.target.name;
    }
}
exports.CrudError = CrudError;
/** errorCode='not_found' — entity с таким id не существует. */
class NotFoundError extends CrudError {
    static code = 'not_found';
    constructor(message, details) {
        super(NotFoundError.code, message, details);
    }
}
exports.NotFoundError = NotFoundError;
/** errorCode='conflict' — нарушение уникальности (username занят и т.п.). */
class ConflictError extends CrudError {
    static code = 'conflict';
    constructor(message, details) {
        super(ConflictError.code, message, details);
    }
}
exports.ConflictError = ConflictError;
/** errorCode='validation_error' — поля payload не прошли валидацию. */
class ValidationError extends CrudError {
    static code = 'validation_error';
    constructor(message, details) {
        super(ValidationError.code, message, details);
    }
}
exports.ValidationError = ValidationError;
/** errorCode='forbidden' — авторизация (cross-tenant, недостаточная роль). */
class ForbiddenError extends CrudError {
    static code = 'forbidden';
    constructor(message, details) {
        super(ForbiddenError.code, message, details);
    }
}
exports.ForbiddenError = ForbiddenError;
/**
 * Reply из bus.request(Route, dto) перед использованием. Если `isOk: false` —
 * кидает typed exception (`NotFoundError`/`ConflictError`/`ValidationError`/
 * `ForbiddenError`) либо generic `CrudError` для unknown кодов (включая
 * domain-extended).
 *
 * После вызова TS narrowing'ает reply до `isOk: true` (через assert-сигнатуру).
 *
 * @example
 *   const reply = await bus.request(FooRoutes.update, { id, patch: {...} });
 *   throwOnCrudError(reply);   // throw если isOk:false
 *   // здесь reply гарантированно {isOk: true, ...}
 */
function throwOnCrudError(reply) {
    if (reply.isOk)
        return;
    // С этого места reply имеет isOk:false ⇒ есть errorCode (по нашему контракту).
    const errReply = reply;
    const ExceptionClass = pickException(errReply.errorCode);
    if (ExceptionClass !== null) {
        throw new ExceptionClass(errReply.error, errReply.details);
    }
    // Unknown errorCode (domain-specific без registerCrudException) — generic
    // CrudError с реальным кодом. Caller отличит через `err.code`, не через
    // `instanceof`, но base-class ловится `e instanceof CrudError` всегда.
    throw new CrudError(errReply.errorCode, errReply.error, errReply.details);
}
/**
 * Маппинг `errorCode` → класс exception'а. Domain-расширения регистрируются
 * через `registerCrudException(code, ExceptionClass)` — clients-домены могут
 * добавлять свои subclass'ы (например `AlreadyClosedError` для Session).
 */
const exceptionRegistry = new Map([
    ['not_found', NotFoundError],
    ['conflict', ConflictError],
    ['validation_error', ValidationError],
    ['forbidden', ForbiddenError],
]);
const pickException = (code) => {
    return exceptionRegistry.get(code) ?? null;
};
/**
 * Зарегистрировать domain-specific exception класс для конкретного errorCode.
 * Вызывается на module-load уровне `@omniflow/client-<domain>` пакета.
 *
 * @example
 *   class AlreadyClosedError extends CrudError {
 *     static readonly code = 'already_closed' as const;
 *     constructor(message?: string, details?: Record<string, unknown>) {
 *       super(AlreadyClosedError.code, message, details);
 *     }
 *   }
 *   registerCrudException('already_closed', AlreadyClosedError);
 */
const registerCrudException = (code, ExceptionClass) => {
    exceptionRegistry.set(code, ExceptionClass);
};
exports.registerCrudException = registerCrudException;
//# sourceMappingURL=exceptions.js.map